import React, { useEffect, useMemo, useState } from "react";

import {
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyTestExpectationField,
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyTestLabelHierarchyNodeMappingPackage,
	FrontendCompanyTestLabels,
	FrontendCompanyTestLabelSecondaryLabels,
	FrontendCompanyTestPackage,
	FrontendCompanyTestResultField,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleAdditionalInfoMapping,
	FrontendSampleInstance,
	FrontendSampleInstanceAdditionalInfoMapping,
	FrontendSampleTestInformation,
	TestHierarchyTreeNode,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";

import {
	CompanyLabelType,
	SecondaryCompanyLabelType,
	Severity,
	TestHierarchyNodeType,
} from "node-library/dist/constants";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Divider, IconButton, Tooltip } from "@mui/material";
import { NeutralColor, SemanticColor } from "node-library/dist/colors";

import { areStringArraysEqual } from "node-library/dist/helpers/misc_helper";
import { CollapsibleCard } from "../../../../common-components/card";
import {
	CustomCheckbox,
	CustomTextInput,
} from "../../../../common-components/inputs";
import {
	AlertIcon,
	InfoIcon,
	PaperClipIcon,
} from "../../../../common-components/svgIcons";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../../common-components/toast/successIcons";
import { YellowWarnIcon } from "../../../../common-components/toast/warnIcons";
import { downloadSpecificationFile } from "../../../../helpers/frontendFileOperationHelper";
import {
	getAdditionalInfoValueDisplayText,
	getShortTurnaroundTime,
	getTurnaroundTimeBackgroundColor,
} from "../../../../helpers/sampleHelper";
import { renderEnabledTestInfoSection } from "../../../../helpers/testHierarchyHelper";
import {
	getLabelInputKey,
	LabelInputState,
} from "../../../../helpers/testLabelHelper";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;

	orderTestCategory: FrontendCompanyTestCategory;
	hierarchyNodeParentMap: Map<string, FrontendCompanyTestHierarchyNode[]>;
	instanceHierarchyTree: TestHierarchyTreeNode;
	matchingLabelAndPackages: Array<{
		label: FrontendCompanyTestLabels;
		matchingPackages: Array<FrontendCompanyTestLabelHierarchyNodeMappingPackage>;
	}>;
	sampleInstance: FrontendSampleInstance;

	selected: boolean;
	setSelected: (selected: boolean) => void;

	labelInputMap: LabelInputState;
	handleLabelInputChange: (
		sampleInstanceId: string,
		labelId: string,
		secondaryLabelId: string,

		packageId: string,
		value: string,
	) => void;
	openSpecReadingDrawer: () => void;
}

export default function PrintLabelSampleCard(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		company,
		sampleInstance,

		selected,
		setSelected,
		orderTestCategory,
		hierarchyNodeParentMap,
		instanceHierarchyTree,
		matchingLabelAndPackages,
		labelInputMap,
		handleLabelInputChange,
		openSpecReadingDrawer,
	} = props;

	const testInfo: FrontendSampleTestInformation =
		sampleInstance.sampleTestInfo as FrontendSampleTestInformation;

	const selectedPackage: FrontendCompanyTestPackage | undefined =
		orderTestCategory.testPackages.find((pack) => {
			return pack.packageId === sampleInstance.packageId;
		});

	const selectedNodeIds: Array<string> = testInfo.selectedTests.map((test) => {
		return test.selectedNodeId;
	});
	const packageTestNodeIds = selectedPackage?.packageTestNodeIds || [];
	const packageCustomized: boolean =
		packageTestNodeIds.length == 0
			? false
			: !areStringArraysEqual(packageTestNodeIds, selectedNodeIds);
	const selectedMatrix: FrontendCompanySampleMatrix =
		props.orderTestCategory.sampleMatrices.find((matrix) => {
			return matrix.matrixId === sampleInstance.sample.matrixId;
		}) as FrontendCompanySampleMatrix;
	const sampleAdditionalInfos: Array<FrontendSampleAdditionalInfoMapping> =
		sampleInstance.sample.sampleAdditionalInfos;
	const sampleInstanceAdditionalInfos: Array<FrontendSampleInstanceAdditionalInfoMapping> =
		sampleInstance.sampleInstanceAdditionalInfos;

	const labelsRequiringInput: {
		label: FrontendCompanyTestLabels;
		secondaryLabelsNeedingInput: Array<FrontendCompanyTestLabelSecondaryLabels>;
		matchingPackages: Array<FrontendCompanyTestLabelHierarchyNodeMappingPackage>;
	}[] = useMemo(() => {
		return matchingLabelAndPackages
			.map((labelAndPack) => {
				const secondaryLabelsNeedingInput = [];
				for (const secondaryLabel of labelAndPack.label.secondaryLabels) {
					if (
						secondaryLabel.secondaryLabelType ===
						SecondaryCompanyLabelType.INPUT_SEQ_NUMERIC
					) {
						secondaryLabelsNeedingInput.push(secondaryLabel);
					}
				}
				if (secondaryLabelsNeedingInput.length == 0) return null;
				return {
					...labelAndPack,
					secondaryLabelsNeedingInput,
				};
			})
			.filter((label) => {
				return label !== null;
			}) as {
			label: FrontendCompanyTestLabels;
			secondaryLabelsNeedingInput: Array<FrontendCompanyTestLabelSecondaryLabels>;
			matchingPackages: Array<FrontendCompanyTestLabelHierarchyNodeMappingPackage>;
		}[];
	}, [matchingLabelAndPackages]);

	const needInformation: boolean = useMemo(() => {
		if (labelsRequiringInput.length === 0) return false;

		return labelsRequiringInput.some((labelAndPack) => {
			for (const pack of labelAndPack.matchingPackages) {
				for (const secondaryLabel of labelAndPack.secondaryLabelsNeedingInput) {
					const key = getLabelInputKey(
						sampleInstance.sampleInstanceId,
						labelAndPack.label.labelId,
						secondaryLabel.secondaryLabelId,
						pack.packageId,
					);
					const inputsForLabel: string = labelInputMap[key];
					if (!inputsForLabel?.trim()) {
						return true;
					} // empty, undefined, or just spaces = still needs input
				}
			}
			return false;
		});
	}, [labelsRequiringInput, labelInputMap]);

	const [expanded, setExpanded] = useState(needInformation);

	async function handleDownloadSpecification() {
		try {
			if (
				!sampleInstance.specification ||
				!sampleInstance.specification.canDownload
			) {
				return;
			}
			const result: boolean = await downloadSpecificationFile(
				sampleInstance.specification,
			);
			if (!result) {
				openSnackbar(
					translation("FailedToDownloadSpecification"),
					Severity.ERROR,
					translation("SystemErrorMsg"),
					<RedErrorIcon />,
				);
			}
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		}
	}

	const sampleCardContent: React.ReactNode = (
		<div className="print-label-list-row">
			<div onClick={(e) => e.stopPropagation()}>
				<CustomCheckbox
					className={``}
					label={``}
					checked={selected}
					setChecked={() => {
						if (selected) {
							setExpanded(false);
						} else if (needInformation) {
							setExpanded(true);
						}
						setSelected(!selected);
					}}
				/>
			</div>
			<div
				className={`print-label-info-list-row ${
					!selected ? `not-selected` : ``
				}`}
			>
				<div className="print-label-id-number-column">
					{sampleInstance.sample.fullSampleId || ""}
				</div>
				<div className="sample-preview-info-column sample-preview-basic-info">
					<div className="typography-body-01 overflow-ellipsis-text">
						{sampleInstance.sample.sampleName}
					</div>
					<div className="typography-body-02 overflow-ellipsis-text">{`${translation(
						"Lot",
					)}: ${sampleInstance.sample.lotCode}`}</div>
				</div>
				<div className="sample-preview-test-info">
					<div className="sample-preview-info-column overflow-ellipsis-text">
						<Divider
							className="sample-preview-collapsible-divider"
							orientation="horizontal"
							flexItem
						/>
						<div className="typography-body-01 overflow-ellipsis-text ">
							{`${selectedPackage?.packageName || translation("Customize")}${
								packageCustomized ? "(" + translation("Customized") + ")" : ""
							}`}
						</div>
						<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
							<div style={{ display: "flex", gap: "5px" }}>
								<div
									className="typography-body-02 turnaround-time-box"
									style={{
										backgroundColor: getTurnaroundTimeBackgroundColor(
											(
												sampleInstance.sampleTestInfo as FrontendSampleTestInformation
											).turnaroundTime,
										),
									}}
								>
									{getShortTurnaroundTime(
										(
											sampleInstance.sampleTestInfo as FrontendSampleTestInformation
										).turnaroundTime,
										translation,
									)}
								</div>
							</div>
							{sampleInstance.specification ? (
								<>
									<Divider orientation="vertical" flexItem />{" "}
									<div style={{ display: "flex", alignItems: "center" }}>
										{sampleInstance.specification.active ? (
											sampleInstance.specification.isNewSpecification ? (
												<Tooltip
													PopperProps={{
														sx: {
															"& .MuiTooltip-tooltip": {
																backgroundColor: NeutralColor.NEUTRAL_GREY_950,
																padding: "12px 16px",
															},
														},
													}}
													title={`${translation(
														"NewSpecificationFromClientPortal",
													)}`}
													arrow
												>
													<span
														style={{ display: "flex", width: "fit-content" }}
													>
														<InfoIcon
															strokeColor={SemanticColor.INFO_MEDIUM}
														></InfoIcon>
													</span>
												</Tooltip>
											) : (
												<></>
											)
										) : (
											<Tooltip
												PopperProps={{
													sx: {
														"& .MuiTooltip-tooltip": {
															backgroundColor: NeutralColor.NEUTRAL_GREY_950,
															padding: "12px 16px",
														},
													},
												}}
												title={`${translation("SpecificationDeletedError")}`}
												arrow
											>
												<span style={{ display: "flex", width: "fit-content" }}>
													<AlertIcon></AlertIcon>
												</span>
											</Tooltip>
										)}
										<PaperClipIcon />
										<div
											className="typography-body-02 link-like-text"
											onClick={() => {
												openSpecReadingDrawer();
											}}
										>
											{`${sampleInstance.specification.fileName}.${sampleInstance.specification.format}`}
										</div>
									</div>
								</>
							) : (
								<></>
							)}
						</div>
					</div>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "5px",
							color: needInformation
								? SemanticColor.WARNING_300
								: SemanticColor.SUCCESS_300,
							cursor: "pointer",
						}}
						onClick={() => setExpanded(!expanded)}
					>
						{needInformation ? (
							<>
								<YellowWarnIcon></YellowWarnIcon>
								{translation("NeedInformation")}
							</>
						) : (
							<>
								<GreenTickIcon></GreenTickIcon>
								{translation("ReadyForPrinting")}
							</>
						)}
					</div>
				</div>

				<div className="sample-preview-button-column sample-preview-expand-button">
					<IconButton
						onClick={() => {
							setExpanded(!expanded);
						}}
					>
						{expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
					</IconButton>
				</div>
			</div>
		</div>
	);

	const expectationFieldIdToNameMap = new Map<string, string>();
	for (const field of company.testResultAndExpectationFields) {
		const expectationField: FrontendCompanyTestExpectationField | undefined =
			field.expectationField;
		if (!expectationField) {
			continue;
		}
		const fieldId: string = expectationField.expectationFieldId;
		if (field) {
			expectationFieldIdToNameMap.set(fieldId, field.fieldName);
		}
	}

	const fieldsHavingExpectations: FrontendCompanyTestResultField[] =
		company.testResultAndExpectationFields.filter((resultField) => {
			return resultField.expectationField != undefined;
		});

	const testInfoNode: Array<React.ReactNode> = renderEnabledTestInfoSection(
		instanceHierarchyTree,
		"typography-body-02",
		fieldsHavingExpectations,
		testInfo.expectationInputs,
		expectationFieldIdToNameMap,
		company.testResultUnits,
		company.hierarchyNodeMappingPackages,
	);

	const TATBackgroundColor: string = getTurnaroundTimeBackgroundColor(
		testInfo.turnaroundTime,
	);
	const testInfoList: React.ReactNode = (
		<div key={testInfo.turnaroundTime} className="sample-preview-info-column">
			<div style={{ display: "flex" }}>
				<div
					className="typography-body-02 turnaround-time-box"
					style={{ backgroundColor: TATBackgroundColor }}
				>
					{translation(testInfo.turnaroundTime)}
				</div>
			</div>

			{testInfoNode}

			{testInfo.additionalTest && (
				<div className="view-sample-info-text-row typography-body-02">
					<div className="sample-info-row-description">{`${translation(
						"Additional",
					)}: `}</div>
					<div className="wrap-text ">{testInfo.additionalTestDescription}</div>
				</div>
			)}
		</div>
	);

	const collapsibleContent: React.ReactNode = (
		<div>
			{labelsRequiringInput && labelsRequiringInput.length > 0 ? (
				<div className={`print-label-info-input-row`}>
					{labelsRequiringInput.flatMap((labelAndPack) => {
						const matchingPackages = labelAndPack.matchingPackages;
						const inputFields: Array<React.ReactNode> = matchingPackages.map(
							(pack) => {
								const matchingNodes: Array<FrontendCompanyTestHierarchyNode> =
									orderTestCategory.hierarchyNodes
										.filter((node) => {
											return pack.mappingNodeIds.includes(node.nodeId);
										})
										.sort((a, b) => a.nodeLevel - b.nodeLevel);

								let label: string = matchingNodes
									.filter((node) => {
										return node.nodeType === TestHierarchyNodeType.TEST;
									})
									.map((node) => {
										return node.nodeName;
									})
									.join(" ");
								if (labelAndPack.label.labelType === CompanyLabelType.CUSTOM) {
									label += ` ${labelAndPack.label.primaryLabelText}`;
								}
								const inputFields: Array<React.ReactNode> = [];
								for (const secondaryLabel of labelAndPack.secondaryLabelsNeedingInput) {
									switch (secondaryLabel.secondaryLabelType) {
										case SecondaryCompanyLabelType.INPUT_SEQ_NUMERIC:
											label += ` ${translation("Dillution")}`;
											break;
										case SecondaryCompanyLabelType.FIXED_TEXT:
										case SecondaryCompanyLabelType.DILLUTION_AND_TEST_OPTION:
											break;
									}
									const id = getLabelInputKey(
										sampleInstance.sampleInstanceId,
										labelAndPack.label.labelId,
										secondaryLabel.secondaryLabelId,
										pack.packageId,
									);
									const value = labelInputMap[id];

									inputFields.push(
										<CustomTextInput
											id={id}
											key={id}
											value={value}
											setValue={(value: string) => {
												handleLabelInputChange(
													sampleInstance.sampleInstanceId,
													labelAndPack.label.labelId,
													secondaryLabel.secondaryLabelId,

													pack.packageId,
													value,
												);
											}}
											label={label}
											flexDirection="row"
										></CustomTextInput>,
									);
								}
								return inputFields;
							},
						);
						return inputFields;
					})}
				</div>
			) : (
				<></>
			)}

			<div className="print-label-list-row">
				<div className=" sample-preview-number-column">{/* Placeholder*/}</div>

				<div
					className={`print-label-info-list-row sample-preview-collapsible-content`}
				>
					<div className="print-label-id-number-column">{/* Placeholder*/}</div>

					<div className="sample-preview-info-column sample-preview-expanded-basic-info-column">
						<div className="typography-body-02 typography-bold view-sample-info-image-row">
							<div className="sample-information-icon sample-details-icon"></div>
							<div>{translation("SampleDetails")}</div>
						</div>
						<div className="typography-body-02 view-sample-info-text-row">
							{`${translation("Matrix")}: `}
							<span className="view-sample-info-info-text">
								{sampleInstance.sample.isCustomMatrix
									? sampleInstance.sample.matrix
									: `${selectedMatrix.subMatrix}`}
							</span>
						</div>
						<div className="typography-body-02 view-sample-info-text-row">
							{`${translation("SampleSize")}: `}
							<span className="view-sample-info-info-text">
								{`${sampleInstance.weight} ${sampleInstance.weightUnit}`}
							</span>
						</div>
						<div className="typography-body-02 view-sample-info-text-row">
							{`${translation("SampleDescription")}: `}
							<span className="view-sample-info-info-text wrap-text">
								{sampleInstance.sample.sampleDescription}
							</span>
						</div>
						<div className="typography-body-02 view-sample-info-text-row">
							{`${translation("Retestsample")}: `}
							<span className="view-sample-info-info-text">
								{sampleInstance.isRetest
									? translation("Yes")
									: translation("No")}
							</span>
						</div>
						<div className="typography-body-02 view-sample-info-text-row">
							{`${translation("Additionaltest")}: `}
							<span className="view-sample-info-info-text">
								{sampleInstance.isAdditionalTest
									? translation("Yes")
									: translation("No")}
							</span>
						</div>
						<div className="typography-body-02 view-sample-info-text-row">
							{`${translation("Treatment")}: `}
							<span className="view-sample-info-info-text">
								{sampleInstance.treatment
									? translation("Yes")
									: translation("No")}
							</span>
						</div>
						{sampleAdditionalInfos.map((info) => {
							return (
								<div className="typography-body-02 view-sample-info-text-row">
									{`${info.additionalFieldName}: `}
									<div className="view-sample-info-info-text">
										{getAdditionalInfoValueDisplayText(info)}
									</div>
								</div>
							);
						})}
						{sampleInstanceAdditionalInfos.map((info) => {
							return (
								<div className="typography-body-02 view-sample-info-text-row">
									{`${info.additionalFieldName}: `}
									<div className="view-sample-info-info-text">
										{getAdditionalInfoValueDisplayText(info)}
									</div>
								</div>
							);
						})}
						{/* <div className="typography-body-02 view-sample-info-text-row">
						{`${translation("Showsampleingallery")}: `}
						<span className="view-sample-info-info-text">
							{sampleBatch.showInGallery
								? translation("Yes")
								: translation("No")}
						</span>
					</div> */}
						{sampleInstance.otherInstructions && (
							<div className="typography-body-02 view-sample-info-text-row">
								{`${translation("OtherInstructions")}: `}
								<span className="view-sample-info-info-text wrap-text">
									{sampleInstance.otherInstructions}
								</span>
							</div>
						)}
					</div>

					<div className="sample-preview-info-column overflow-ellipsis-text sample-preview-expanded-test-info-column">
						<div className="typography-body-02 typography-bold view-sample-info-image-row">
							<div className="sample-information-icon test-tat-icon"></div>
							<div>{translation("TestsAndTAT")}</div>
						</div>

						{testInfoList}
					</div>
				</div>
			</div>
		</div>
	);
	const sampleCard: React.ReactNode = (
		<CollapsibleCard
			className="common-card"
			expanded={expanded}
			content={sampleCardContent}
			collapsibleContent={collapsibleContent}
		></CollapsibleCard>
	);

	return sampleCard;
}
